import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Slideout, Button, TextField, Select, Icon, CircularProgress, Skeleton } from "@ann/web-components";
import RichTextEditor from "./RichTextEditor";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import utc from "dayjs/plugin/utc";
import {
  SlideOutContainer,
  Header,
  Title,
  ActionsRow,
  ActionButton,
  ActionIconButton,
  ContentArea,
  Section,
  SectionHeader,
  SectionTitle,
  SectionCount,
  DescriptionText,
  DetailsGrid,
  DetailRow,
  DetailLabel,
  DetailValue,
  CategoryBadge,
  StatusBadge,
  VisibilityValue,
  AuthorValue,
  AuthorAvatar,
  AuthorName,
  AuthorTimestamp,
  AuthorSeparator,
  TagsContainer,
  Tag,
  TimelineList,
  TimelineItem,
  TimelineIcon,
  TimelineContent,
  TimelineLabel,
  TimelineDate,
  AttachmentsList,
  AttachmentItem,
  AttachmentIcon,
  AttachmentInfo,
  AttachmentName,
  AttachmentSize,
  LinkedEntitiesList,
  LinkedEntityItem,
  LinkedEntityIcon,
  LinkedEntityInfo,
  LinkedEntityName,
  LinkedEntityMeta,
  LinkedEntityAction,
  EmptyState,
  FormContainer,
  FormSection,
  FormLabel,
  ErrorMessage,
  InlineCategorySelect,
  SaveCancelRow,
  CompactEditorWrapper,
  SkeletonBlock,
  SkeletonRow,
  SkeletonSection,
} from "./NotesSlideOut.styled";

dayjs.extend(relativeTime);
dayjs.extend(utc);

// Parse API date as UTC when it has no timezone (avoids "in 5 hours" for recent notes)
const parseNoteDate = (dateStr) => {
  if (!dateStr) return null;
  const s = String(dateStr);
  const hasTimezone = s.endsWith("Z") || /[+-]\d{2}:?\d{2}$/.test(s);
  return hasTimezone ? dayjs(dateStr) : dayjs.utc(dateStr).local();
};

// Category badge colors
const CATEGORY_STYLES = {
  FINANCE: { bg: '#FEF3C7', color: '#D97706' },
  SERVICE: { bg: '#D1FAE5', color: '#059669' },
  SALES: { bg: '#DBEAFE', color: '#2563EB' },
  GENERAL: { bg: '#E0E7FF', color: '#4F46E5' },
  OPPORTUNITY: { bg: '#FCE7F3', color: '#DB2777' },
  PURCHASE: { bg: '#CCFBF1', color: '#0D9488' },
};

const NotesSlideOut = ({
  open,
  onClose,
  showBackButton = false,
  mode = "create",
  noteData = null,
  initialEditMode = false,
  isLoading = false,
  onCreateNote,
  onUpdateNote,
  onDeleteNote,
  onPinNote,
}) => {
  const isCreateMode = mode === "create";
  const { contactId } = useParams();

  // View vs Edit mode for existing notes
  const [isEditing, setIsEditing] = useState(false);
  
  // Form state
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [noteUuid, setNoteUuid] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Reset state when opening
  useEffect(() => {
    if (open && isCreateMode) {
      setTitle("");
      setContent("");
      setSelectedCategory("GENERAL");
      setNoteUuid(null);
      setError(null);
      setIsEditing(true); // Always edit mode for create
    } else if (open && !isCreateMode && noteData) {
      setTitle(noteData.note_title || noteData.title || "");
      setContent(noteData.note_content || noteData.description || "");
      setSelectedCategory(noteData.category || noteData.categories?.[0] || "GENERAL");
      const uuid = 
        noteData.note_uuid || 
        noteData.uuid || 
        noteData.noteUuid ||
        noteData.raw?.note_uuid ||
        noteData.raw?.uuid ||
        null;
      setNoteUuid(uuid);
      setError(null);
      setIsEditing(initialEditMode); // Edit from menu opens directly in edit mode
    }
  }, [open, isCreateMode, noteData, initialEditMode]);

  // Parsed note data for display
  const displayData = useMemo(() => {
    if (!noteData) return null;
    
    const raw = noteData.raw || {};
    
    const ownerName = 
      noteData.ownerFullName ||
      raw.ownerFullName ||
      noteData.owner_name ||
      raw.owner_name ||
      noteData.owner_full_name || 
      raw.owner_full_name ||
      noteData.owner?.name || 
      raw.owner?.name ||
      "Unknown";
    
    const ownerAvatar = 
      noteData.ownerAvatar ||
      raw.ownerAvatar ||
      noteData.owner_avatar || 
      raw.owner_avatar ||
      noteData.owner?.avatar || 
      raw.owner?.avatar ||
      null;
    
    const createdAt = 
      noteData.createdAt ||
      raw.createdAt ||
      noteData.created_at ||
      raw.created_at ||
      null;
    
    const lastUpdated = 
      noteData.lastUpdate ||
      raw.lastUpdate ||
      noteData.updated_at ||
      raw.updated_at ||
      noteData.last_update || 
      raw.last_update ||
      noteData.lastUpdated ||
      raw.lastUpdated ||
      null;
    
    return {
      title: noteData.noteTitle || raw.noteTitle || noteData.note_title || raw.note_title || noteData.title || raw.title || raw.name || "Untitled",
      description: noteData.description || raw.description || noteData.note_content || raw.note_content || raw.content || "",
      category: noteData.category || raw.category || noteData.categories?.[0]?.category_uuid || noteData.categories?.[0] || raw.categories?.[0]?.category_uuid || raw.categories?.[0] || "GENERAL",
      visibility: noteData.visibility || raw.visibility || "PRIVATE",
      isTeamNote: noteData.isTeamNote || noteData.is_team_note || raw.isTeamNote || raw.is_team_note,
      status: noteData.status || raw.status || null,
      isPinned: noteData.isPinned || noteData.is_pinned || raw.isPinned || raw.is_pinned || false,
      owner: {
        name: ownerName,
        avatar: ownerAvatar,
      },
      tags: noteData.tags || raw.tags || [],
      createdAt,
      lastUpdated,
      attachments: noteData.attachments || raw.attachments || [],
      linkedEntities: noteData.linkedEntities || noteData.linked_entities || raw.linkedEntities || raw.linked_entities || [],
    };
  }, [noteData]);

  // Format date for display (full format; parse as UTC when API sends without timezone)
  const formatDate = useCallback((date) => {
    const d = parseNoteDate(date);
    return d && d.isValid() ? d.format("MMMM D, YYYY [at] h:mm A") : "N/A";
  }, []);

  // Format date as relative time (e.g., "12 days ago")
  const formatRelativeDate = useCallback((date) => {
    const d = parseNoteDate(date);
    return d && d.isValid() ? d.fromNow() : null;
  }, []);

  // Get category style
  const getCategoryStyle = useCallback((category) => {
    const cat = (category || "GENERAL").toUpperCase();
    return CATEGORY_STYLES[cat] || CATEGORY_STYLES.GENERAL;
  }, []);

  // Handle form submit
  const handleSubmit = useCallback(async () => {
    if (!title.trim()) {
      setError("Title is required");
      return;
    }
    if (!content.trim()) {
      setError("Content is required");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      if (isCreateMode) {
        if (!onCreateNote) {
          console.warn("onCreateNote handler is not provided");
          return;
        }

        const payload = {
          entity_type: "CONTACTS",
          entity_uuid: contactId,
          note_title: title.trim(),
          note_content: content.trim(),
          categories: selectedCategory ? [selectedCategory] : [],
          tags: [],
        };

        await onCreateNote(payload);
        onClose();
      } else {
        if (!onUpdateNote) {
          console.warn("onUpdateNote handler is not provided");
          return;
        }

        const uuidToUse = noteUuid || noteData?.note_uuid || noteData?.raw?.note_uuid;

        if (!uuidToUse) {
          setError("Note UUID is required for update");
          return;
        }

        const payload = {
          note_uuid: uuidToUse,
          note_title: title.trim(),
          note_content: content.trim(),
          categories: selectedCategory ? [selectedCategory] : [],
          tags: [],
        };

        await onUpdateNote(payload);
        setIsEditing(false);
      }
    } catch (err) {
      console.error(`Error ${isCreateMode ? "creating" : "updating"} note:`, err);
      setError(
        err.message ||
          `Failed to ${isCreateMode ? "create" : "update"} note. Please try again.`
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [
    title,
    content,
    selectedCategory,
    contactId,
    isCreateMode,
    noteUuid,
    noteData,
    onCreateNote,
    onUpdateNote,
    onClose,
  ]);

  // Handle actions
  const handleEdit = useCallback(() => {
    // Populate form with current values
    setTitle(displayData?.title || "");
    setContent(displayData?.description || "");
    setSelectedCategory(displayData?.category || "GENERAL");
    setIsEditing(true);
  }, [displayData]);

  const handleCancelEdit = useCallback(() => {
    setIsEditing(false);
    setError(null);
  }, []);

  const handlePin = useCallback(() => {
    if (onPinNote && noteUuid) {
      onPinNote(noteUuid, !displayData?.isPinned);
    }
  }, [onPinNote, noteUuid, displayData?.isPinned]);

  const handleCopyLink = useCallback(() => {
    const url = `${window.location.origin}${window.location.pathname}?note=${noteUuid}`;
    navigator.clipboard.writeText(url);
  }, [noteUuid]);

  const handleDelete = useCallback(async () => {
    if (onDeleteNote && noteUuid) {
      if (window.confirm("Are you sure you want to delete this note?")) {
        await onDeleteNote(noteUuid);
        onClose();
      }
    }
  }, [onDeleteNote, noteUuid, onClose]);

  // Strip HTML tags for plain text display
  const stripHtml = (html) => {
    if (!html) return "";
    return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
  };

  // Render create mode (separate form for new notes)
  const renderCreateMode = () => (
    <>
      <Header>
        <Title>Create Note</Title>
      </Header>

      <ContentArea>
        <FormContainer>
          {error && <ErrorMessage>{error}</ErrorMessage>}
          
          <FormSection>
            <FormLabel>Title</FormLabel>
            <TextField
              value={title}
              onChange={setTitle}
              size="medium"
              placeholder="Enter note title"
              disabled={isSubmitting}
            />
          </FormSection>

          <FormSection>
            <FormLabel>Category</FormLabel>
            <Select
              size="medium"
              value={selectedCategory}
              onChange={setSelectedCategory}
              disabled={isSubmitting}
              placeholder="Select category"
            >
              <Select.Menu>
                <Select.Item label="Finance" value="FINANCE">Finance</Select.Item>
                <Select.Item label="Opportunity" value="OPPORTUNITY">Opportunity</Select.Item>
                <Select.Item label="Purchase" value="PURCHASE">Purchase</Select.Item>
                <Select.Item label="Service" value="SERVICE">Service</Select.Item>
                <Select.Item label="Sales" value="SALES">Sales</Select.Item>
                <Select.Item label="General" value="GENERAL">General</Select.Item>
              </Select.Menu>
            </Select>
          </FormSection>

          <FormSection>
            <FormLabel>Content</FormLabel>
            <RichTextEditor
              content={content}
              onChange={setContent}
              disabled={isSubmitting}
            />
          </FormSection>
        </FormContainer>
      </ContentArea>

      <SaveCancelRow>
        <Button
          label="Create Note"
          type="primary"
          size="medium"
          onClick={handleSubmit}
          disabled={isSubmitting}
          loading={isSubmitting}
        />
      </SaveCancelRow>
    </>
  );

  // Render unified view/edit mode (inline editing for existing notes)
  const renderViewEditMode = () => (
    <>
      {/* Header */}
      <Header>
        {isLoading && <Skeleton height="20px" width="200px" />}
        {!isLoading && (
          <Title>
            {isEditing ? "Edit Note" : displayData?.title || "Untitled"}
          </Title>
        )}
      </Header>

      {/* Actions Row */}
      <ActionsRow>
        {isEditing ? (
          <>
            <ActionButton onClick={handleCancelEdit} disabled={isSubmitting}>
              Cancel
            </ActionButton>
            <ActionButton
              onClick={handleSubmit}
              disabled={isSubmitting}
              style={{
                background: "#155DFC",
                color: "#fff",
                borderColor: "#155DFC",
              }}
            >
              {isSubmitting ? "Saving..." : "Save"}
            </ActionButton>
          </>
        ) : (
          <>
            <ActionButton onClick={handleEdit}>
              <Icon name="edit" width="16px" height="16px" />
              Edit
            </ActionButton>
            <ActionButton onClick={handlePin}>
              <Icon name="star" width="16px" height="16px" />
              {displayData?.isPinned ? "Unpin" : "Pin"}
            </ActionButton>
            <ActionButton onClick={handleCopyLink}>
              <Icon name="contentCopy" width="16px" height="16px" />
              Copy link
            </ActionButton>
            <ActionIconButton $marginLeft>
              <Icon name="folder" width="18px" height="18px" />
            </ActionIconButton>
            <ActionIconButton $danger onClick={handleDelete}>
              <Icon name="delete" width="18px" height="18px" />
            </ActionIconButton>
          </>
        )}
      </ActionsRow>

      {/* Error message */}
      {error && (
        <div style={{ padding: "0 24px" }}>
          <ErrorMessage>{error}</ErrorMessage>
        </div>
      )}

      {/* Content */}
      <ContentArea>
        {/* Title Section - Only shown when editing */}
        {isEditing && (
          <Section>
            <SectionHeader>
              <SectionTitle>Title</SectionTitle>
            </SectionHeader>
            <TextField
              value={title}
              onChange={setTitle}
              size="medium"
              placeholder="Enter note title"
              disabled={isSubmitting}
            />
          </Section>
        )}

        {/* Description Section */}
        <Section>
          <SectionHeader>
            <SectionTitle>Description</SectionTitle>
          </SectionHeader>
          {isEditing ? (
            <CompactEditorWrapper>
              <RichTextEditor
                content={content}
                onChange={setContent}
                disabled={isSubmitting}
              />
            </CompactEditorWrapper>
          ) : isLoading ? (
            <SkeletonBlock>
              <Skeleton height="16px" width="100%" />
              <Skeleton height="16px" width="95%" />
              <Skeleton height="16px" width="70%" />
            </SkeletonBlock>
          ) : (
            <DescriptionText>
              {stripHtml(displayData?.description) ||
                "No description provided."}
            </DescriptionText>
          )}
        </Section>

        {/* Details Section */}
        <Section>
          <SectionHeader>
            <SectionTitle>Details</SectionTitle>
          </SectionHeader>
          {isLoading ? (
            <SkeletonSection>
              <SkeletonRow>
                <Skeleton height="14px" width="64px" />
                <Skeleton height="24px" width="100px" />
              </SkeletonRow>
              <SkeletonRow>
                <Skeleton height="14px" width="56px" />
                <Skeleton height="20px" width="80px" />
              </SkeletonRow>
              <SkeletonRow>
                <Skeleton height="14px" width="72px" />
                <Skeleton height="20px" width="140px" />
              </SkeletonRow>
              <SkeletonRow>
                <Skeleton height="14px" width="56px" />
                <Skeleton height="20px" width="120px" />
              </SkeletonRow>
            </SkeletonSection>
          ) : (
          <DetailsGrid>
            {/* Category - Editable inline */}
            <DetailRow>
              <DetailLabel>Category</DetailLabel>
              <DetailValue>
                {isEditing ? (
                  <InlineCategorySelect>
                    <Select
                      size="small"
                      value={selectedCategory}
                      onChange={setSelectedCategory}
                      disabled={isSubmitting}
                    >
                      <Select.Menu>
                        <Select.Item label="Finance" value="FINANCE">
                          Finance
                        </Select.Item>
                        <Select.Item label="Opportunity" value="OPPORTUNITY">
                          Opportunity
                        </Select.Item>
                        <Select.Item label="Purchase" value="PURCHASE">
                          Purchase
                        </Select.Item>
                        <Select.Item label="Service" value="SERVICE">
                          Service
                        </Select.Item>
                        <Select.Item label="Sales" value="SALES">
                          Sales
                        </Select.Item>
                        <Select.Item label="General" value="GENERAL">
                          General
                        </Select.Item>
                      </Select.Menu>
                    </Select>
                  </InlineCategorySelect>
                ) : displayData?.category ? (
                  <CategoryBadge
                    $bgColor={getCategoryStyle(displayData.category).bg}
                    $color={getCategoryStyle(displayData.category).color}
                  >
                    {displayData.category.toUpperCase()}
                  </CategoryBadge>
                ) : (
                  <span style={{ color: "#9CA3AF" }}>Not set</span>
                )}
              </DetailValue>
            </DetailRow>

            {/* Visibility */}
            <DetailRow>
              <DetailLabel>Visibility</DetailLabel>
              <DetailValue>
                <VisibilityValue>
                  <Icon
                    name={
                      displayData?.isTeamNote ||
                      displayData?.visibility === "TEAM"
                        ? "group"
                        : "visibility"
                    }
                    width="16px"
                    height="16px"
                  />
                  {displayData?.isTeamNote || displayData?.visibility === "TEAM"
                    ? "Team"
                    : "Private"}
                </VisibilityValue>
              </DetailValue>
            </DetailRow>

            {/* Status */}
            {displayData?.status && (
              <DetailRow>
                <DetailLabel>Status</DetailLabel>
                <DetailValue>
                  <StatusBadge>{displayData.status.toLowerCase()}</StatusBadge>
                </DetailValue>
              </DetailRow>
            )}

            {/* Created by */}
            <DetailRow>
              <DetailLabel>Created by</DetailLabel>
              <DetailValue>
                <AuthorValue>
                  <AuthorAvatar>
                    {displayData?.owner?.avatar ? (
                      <img
                        src={displayData.owner.avatar}
                        alt={displayData.owner.name}
                      />
                    ) : (
                      <Icon name="person" width="16px" height="16px" />
                    )}
                  </AuthorAvatar>
                  <AuthorName>
                    {displayData?.owner?.name || "Unknown"}
                  </AuthorName>
                  {formatRelativeDate(displayData?.createdAt) && (
                    <AuthorTimestamp>
                      {formatRelativeDate(displayData?.createdAt)}
                    </AuthorTimestamp>
                  )}
                  {formatRelativeDate(displayData?.lastUpdated) &&
                    displayData?.lastUpdated !== displayData?.createdAt && (
                      <>
                        <AuthorSeparator>•</AuthorSeparator>
                        <AuthorTimestamp>
                          Edited {formatRelativeDate(displayData?.lastUpdated)}
                        </AuthorTimestamp>
                      </>
                    )}
                </AuthorValue>
              </DetailValue>
            </DetailRow>

            {/* Tags */}
            {displayData?.tags?.length > 0 && (
              <DetailRow>
                <DetailLabel>Tags</DetailLabel>
                <DetailValue>
                  <TagsContainer>
                    {displayData.tags.map((tag, index) => (
                      <Tag key={index}>{tag}</Tag>
                    ))}
                  </TagsContainer>
                </DetailValue>
              </DetailRow>
            )}
          </DetailsGrid>
          )}
        </Section>

        {/* Timeline Section */}
        <Section>
          <SectionHeader>
            <SectionTitle>Timeline</SectionTitle>
          </SectionHeader>
          {isLoading ? (
            <SkeletonSection>
              <SkeletonRow>
                <Skeleton height="20px" width="20px" />
                <SkeletonBlock>
                  <Skeleton height="14px" width="56px" />
                  <Skeleton height="13px" width="160px" />
                </SkeletonBlock>
              </SkeletonRow>
              <SkeletonRow>
                <Skeleton height="20px" width="20px" />
                <SkeletonBlock>
                  <Skeleton height="14px" width="80px" />
                  <Skeleton height="13px" width="160px" />
                </SkeletonBlock>
              </SkeletonRow>
            </SkeletonSection>
          ) : (
          <TimelineList>
            <TimelineItem>
              <TimelineIcon>
                <Icon name="schedule" width="18px" height="18px" />
              </TimelineIcon>
              <TimelineContent>
                <TimelineLabel>Created</TimelineLabel>
                <TimelineDate>
                  {formatDate(displayData?.createdAt)}
                </TimelineDate>
              </TimelineContent>
            </TimelineItem>
            <TimelineItem>
              <TimelineIcon>
                <Icon name="schedule" width="18px" height="18px" />
              </TimelineIcon>
              <TimelineContent>
                <TimelineLabel>Last updated</TimelineLabel>
                <TimelineDate>
                  {formatDate(displayData?.lastUpdated)}
                </TimelineDate>
              </TimelineContent>
            </TimelineItem>
          </TimelineList>
          )}
        </Section>

        {/* Attachments Section */}
        <Section>
          <SectionHeader>
            <SectionTitle>Attachments</SectionTitle>
            {!isLoading && (
              <SectionCount>{displayData?.attachments?.length || 0}</SectionCount>
            )}
          </SectionHeader>
          {isLoading ? (
            <SkeletonSection>
              <Skeleton height="40px" width="100%" />
              <Skeleton height="40px" width="90%" />
            </SkeletonSection>
          ) : displayData?.attachments?.length > 0 ? (
            <AttachmentsList>
              {displayData.attachments.map((attachment, index) => (
                <AttachmentItem key={index}>
                  <AttachmentIcon>
                    <Icon name="attachFile" width="18px" height="18px" />
                  </AttachmentIcon>
                  <AttachmentInfo>
                    <AttachmentName>
                      {attachment.name || `Attachment ${index + 1}`}
                    </AttachmentName>
                    {attachment.size && (
                      <AttachmentSize>{attachment.size}</AttachmentSize>
                    )}
                  </AttachmentInfo>
                </AttachmentItem>
              ))}
            </AttachmentsList>
          ) : (
            <EmptyState>No attachments</EmptyState>
          )}
        </Section>

        {/* Linked Entities Section */}
        <Section>
          <SectionHeader>
            <SectionTitle>Linked Entities</SectionTitle>
            {!isLoading && (
              <SectionCount>
                {displayData?.linkedEntities?.length || 0}
              </SectionCount>
            )}
          </SectionHeader>
          {isLoading ? (
            <SkeletonSection>
              <Skeleton height="48px" width="100%" />
              <Skeleton height="48px" width="85%" />
            </SkeletonSection>
          ) : displayData?.linkedEntities?.length > 0 ? (
            <LinkedEntitiesList>
              {displayData.linkedEntities.map((entity, index) => (
                <LinkedEntityItem key={index}>
                  <LinkedEntityIcon>
                    <Icon name="link" width="18px" height="18px" />
                  </LinkedEntityIcon>
                  <LinkedEntityInfo>
                    <LinkedEntityName>
                      {entity.name || `Entity ${index + 1}`}
                    </LinkedEntityName>
                    <LinkedEntityMeta>
                      {entity.type || "Entity"} • {entity.status || "Active"}
                    </LinkedEntityMeta>
                  </LinkedEntityInfo>
                  <LinkedEntityAction>
                    <Icon name="help" width="16px" height="16px" />
                  </LinkedEntityAction>
                </LinkedEntityItem>
              ))}
            </LinkedEntitiesList>
          ) : (
            <EmptyState>No linked entities</EmptyState>
          )}
        </Section>
      </ContentArea>
    </>
  );

  return (
    <Slideout open={open} onClose={onClose}>
      <SlideOutContainer>
        {isCreateMode ? renderCreateMode() : renderViewEditMode()}
      </SlideOutContainer>
    </Slideout>
  );
};

export default NotesSlideOut;
